import { ChangeDetectionStrategy, Component, DestroyRef, Inject, signal, effect } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { init } from '@sentry/angular';
import { privateDecrypt } from 'crypto';
import { ConfigurationSaisieDonneesAnalyse, ModeSaisie } from 'src/app/bo/configurationSaisieDonneesAnalyse';
import { DonneeAnalyse } from 'src/app/bo/donneeAnalyse';
import { UnitDefinitionDto } from 'src/app/bo/unitDefinition';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { KpiService } from 'src/app/services/kpi.service';
import { TraductionService } from 'src/app/services/traduction.service';
import { TypeSaisieKpi } from 'src/app/utils/type-saisie-kpi';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-modify-kpi-modal',
  templateUrl: './modify-kpi-modal.component.html',
  styleUrls: ['./modify-kpi-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ModifyKpiModalComponent {
  readonly configForm = signal<FormGroup>(null);
  readonly toggleLabl = signal<string>('');
  readonly configView = signal<boolean>(true);
  readonly autorisePourUtil = signal<boolean>(false);
  readonly typeSaisieKpi = signal<TypeSaisieKpi>('InputText');
  readonly tabModeSaisieProjet = signal<any[]>([]);
  readonly availableUnits = signal<UnitDefinitionDto[]>([]);
  
  constructor(
    private readonly dialog: MatDialog,
    public dialogRef: MatDialogRef<ModifyKpiModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly fonctionsService: FonctionsService,
    private readonly kpiService: KpiService,
    private readonly traductionService: TraductionService,
    private readonly destroyRef: DestroyRef,
  ) { 
   kpiService.getCalculModeSaisieProjet().pipe(
     takeUntilDestroyed(this.destroyRef)
   ).subscribe(
      (tabModeSaisie) => {
        this.tabModeSaisieProjet.set(tabModeSaisie);
      });    
    }


  ngOnInit(): void {

    this.initForm(this.data.kpiConfig, this.data.kpiBlast);
    this.configView.set(this.data.configView);
    this.autorisePourUtil.set(this.configView() ? true : this.data.kpiBlast?.autorisePourUtil);
    this.setFormAccess();
    this.typeSaisieKpi.set(this.data.kpiConfig.referentielDonneeAnalyse.typeSaisieKpi);
    if (this.configView() && this.data.kpiConfig.quantityType) {
      this.loadUnits(this.data.kpiConfig.quantityType);
    }
  }

  private initForm(configKip: ConfigurationSaisieDonneesAnalyse, kipBlast: DonneeAnalyse): void {
    const formGroup = new FormGroup({
      type: new FormControl({ value: configKip.referentielDonneeAnalyse.type, disabled: true }),
      name: new FormControl({ value: configKip.referentielDonneeAnalyse.nom, disabled: true }),
      unit: new FormControl(configKip.displayUnit),
      targetValue: new FormControl(configKip.valeurNominal),
      targetValueSite: new FormControl(configKip.valeurNominal),
      targetValueBlast: new FormControl(kipBlast?.valeur),
      targetValueDesign: new FormControl({ value: kipBlast?.designValue, disabled: true }),
      targetValueMeasured: new FormControl({ value: kipBlast?.measuredValue, disabled: true }),
      targetValueManual: new FormControl(kipBlast?.manualValue),
      minValue: new FormControl(configKip.valeurMin),
      maxValue: new FormControl(configKip.valeurMax),
      calculeMode: new FormControl(configKip.modeSaisieDonneeProjet),
      access: new FormControl(configKip.accessPermission),
      activate: new FormControl(configKip.isEnabled),
    });
    this.configForm.set(formGroup);


    this.traductionService.translate.
      get(formGroup.get('type')?.value).subscribe(translatedType => {
        formGroup.get('type')?.setValue(translatedType.replace(/'/g, ''));
      });

    if (this.configView()) {
      this.traductionService.translate.
        get(formGroup.get('name')?.value).subscribe(translatedType => {
          formGroup.get('name')?.setValue(translatedType.replace(/'/g, ''));
        });
    }
  }

  protected onClose(): void {
    this.dialog.closeAll();
  }

  protected onSubmit(): void {
    if (this.configView()) {
      this.updateConfigDonneeAnalyse();
    } else {
      this.updateDonneeAnalyse();
    }
  }

  protected updateDonneeAnalyse(): void {

    this.fonctionsService.loading_screen(true);
    let updatedDonneeAnalyse = this.getFormDonneeAnalyseValue();
    this.kpiService.updateDonneeAnalyse(updatedDonneeAnalyse).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      response => {
        this.fonctionsService.loading_screen(false);
        this.dialogRef.close(updatedDonneeAnalyse);
        console.log('Donnee Analyse updated successfully:', response);
      },
      error => {
        console.error('Error updating Donnee Analyse:', error);
      }
    );
  }


  protected updateConfigDonneeAnalyse(): void {
    this.fonctionsService.loading_screen(true);
    let updatedConfig: ConfigurationSaisieDonneesAnalyse[] = [];
    updatedConfig.push(this.getFormConfigValue());

    this.kpiService.updateKpiConfiguration(updatedConfig).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      response => {
        this.fonctionsService.loading_screen(false);
        this.dialog.closeAll();
        console.log('Configuration updated successfully:', response);
      },
      error => {
        console.error('Error updating configuration:', error);
      }
    );
  }

  protected getFormConfigValue(): ConfigurationSaisieDonneesAnalyse {
    const form = this.configForm();
    let request = this.data.kpiConfig;
    request.valeurNominal = form.get('targetValue')?.value;
    request.valeurMin = form.get('minValue')?.value;
    request.valeurMax = form.get('maxValue')?.value;
    request.accessPermission = form.get('access')?.value;
    request.isEnabled = form.get('activate')?.value;
    request.modeSaisieDonneeProjet = form.get('calculeMode')?.value;
    request.displayUnit = form.get('unit')?.value;
    return request;
  }

  protected getFormDonneeAnalyseValue(): DonneeAnalyse {
    const form = this.configForm();
    let request = this.data.kpiBlast;
    request.valeur = form.get('targetValueBlast')?.value;
    request.designValue = form.get('targetValueDesign')?.value;
    request.measuredValue = form.get('targetValueMeasured')?.value;
    request.manualValue = form.get('targetValueManual')?.value;
    return request;
  }

  protected resetTargetValueBlast(): void {
    const form = this.configForm();
    form.get('targetValueBlast')?.setValue(form.get('targetValueSite')?.value);
  }

  protected resetManualValue(): void {
    this.configForm().get('targetValueManual')?.setValue('');
  }

  private setFormAccess(): void {
    const form = this.configForm();
    const manualValuecontrol = form.get('targetValueManual');
    !this.autorisePourUtil() ? manualValuecontrol.disable() : manualValuecontrol.enable();

    const targetValueBlastControl = form.get('targetValueBlast');
    !this.autorisePourUtil() ? targetValueBlastControl.disable() : targetValueBlastControl.enable();
  }

  private loadUnits(quantityType: string): void {
    this.kpiService.getUnits(quantityType).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      (units) => {
        this.availableUnits.set(units.map(unit => ({
          ...unit,
          formattedValue: `${unit.unit} - ${unit.symbol}`
        })));
      },
      (error) => {
        console.error('Error loading units:', error);
        this.availableUnits.set([]);
      }
    );
  }
}
